import { useState } from "react";
import { AdminLayout } from "@/components/layout/AdminLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { trpc } from "@/lib/trpc";
import {
  Shield,
  Users,
  Building2,
  ChevronDown,
  ChevronRight,
  Plus,
  X,
  AlertCircle,
  Search,
  Smartphone,
  Globe,
  Hammer,
  PenTool,
} from "lucide-react";

const APP_ICONS: Record<string, React.ElementType> = {
  SAAS: Globe,
  TRAVAUXBTP: Hammer,
  MOBILE: Smartphone,
  ARCHITECT: PenTool,
};

const APP_LABELS: Record<string, string> = {
  SAAS: "SaaS",
  TRAVAUXBTP: "TravauxBTP",
  MOBILE: "Mobile",
  ARCHITECT: "Architect",
};

const ROLE_LABELS: Record<string, string> = {
  OWNER: "Owner",
  PRO: "Pro",
  PARTICULIER: "Particulier",
  DIRIGEANT: "Dirigeant",
  EMPLOYE: "Employé",
  FREEMIUM: "Freemium",
  ARCHITECTE: "Architecte",
};

function CompanyAccessRow({ company }: { company: any }) {
  const [expanded, setExpanded] = useState(false);
  const utils = trpc.useUtils();

  const { data: access, isLoading } = trpc.admin.companies.getCompanyAccess.useQuery(
    { companyId: company.id },
    { enabled: expanded }
  );

  const revoke = trpc.admin.companies.revokeAccess.useMutation({
    onSuccess: () => utils.admin.companies.getCompanyAccess.invalidate({ companyId: company.id }),
  });

  return (
    <div className="border-b last:border-0">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between px-4 py-3 text-sm hover:bg-muted/30 transition-colors text-left"
      >
        <div className="flex items-center gap-3">
          {expanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
          <div>
            <div className="font-medium">{company.name}</div>
            <div className="text-xs text-muted-foreground">{company.usersCount ?? 0} utilisateurs</div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {(company.activeApps || []).map((app: string) => {
            const Icon = APP_ICONS[app] || Globe;
            return (
              <Badge key={app} variant="secondary" className="gap-1">
                <Icon className="h-3 w-3" />
                {APP_LABELS[app] || app}
              </Badge>
            );
          })}
        </div>
      </button>

      {expanded && (
        <div className="px-4 pb-4 pl-11">
          {isLoading ? (
            <div className="space-y-2">
              {Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-8 w-full" />)}
            </div>
          ) : !access?.users?.length ? (
            <p className="text-sm text-muted-foreground">Aucun accès configuré</p>
          ) : (
            <div className="border rounded-lg overflow-hidden">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-muted/50 border-b">
                    <th className="text-left px-3 py-2 font-medium text-muted-foreground">Utilisateur</th>
                    <th className="text-left px-3 py-2 font-medium text-muted-foreground">App</th>
                    <th className="text-left px-3 py-2 font-medium text-muted-foreground">Rôle</th>
                    <th className="text-left px-3 py-2 font-medium text-muted-foreground">Statut</th>
                    <th className="text-right px-3 py-2 font-medium text-muted-foreground">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {access.users.flatMap((u: any) =>
                    (u.accesses || []).map((acc: any) => (
                      <tr key={acc.id} className="hover:bg-muted/20">
                        <td className="px-3 py-2">
                          <div className="font-medium">{[u.firstName, u.lastName].filter(Boolean).join(" ") || "—"}</div>
                          <div className="text-xs text-muted-foreground">{u.email}</div>
                        </td>
                        <td className="px-3 py-2">
                          <Badge variant="outline" className="gap-1">
                            {(() => { const Icon = APP_ICONS[acc.app] || Globe; return <Icon className="h-3 w-3" />; })()}
                            {APP_LABELS[acc.app] || acc.app}
                          </Badge>
                        </td>
                        <td className="px-3 py-2">
                          <span className="text-sm">{ROLE_LABELS[acc.role] || acc.role}</span>
                        </td>
                        <td className="px-3 py-2">
                          <Badge variant={acc.isActive ? "success" : "secondary"}>
                            {acc.isActive ? "Actif" : "Révoqué"}
                          </Badge>
                        </td>
                        <td className="px-3 py-2 text-right">
                          {acc.isActive && (
                            <Button
                              variant="outline"
                              size="sm"
                              disabled={revoke.isPending}
                              onClick={() => {
                                if (confirm("Révoquer cet accès ?")) {
                                  revoke.mutate({ accessId: acc.id });
                                }
                              }}
                            >
                              Révoquer
                            </Button>
                          )}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
              {access.mobileSeatsCount != null && (
                <div className="px-3 py-2 bg-muted/30 text-xs text-muted-foreground border-t">
                  Sièges Mobile : {access.mobileSeatsCount}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function AccessManagement() {
  const [search, setSearch] = useState("");

  const { data: companies, isLoading } = trpc.admin.companies.list.useQuery({
    page: 1,
    limit: 100,
    search: search || undefined,
  } as any);

  const items = (companies as any)?.items || companies || [];

  const appCounts: Record<string, number> = {};
  let multiAppCount = 0;
  for (const c of items) {
    const apps = c.activeApps || [];
    if (apps.length > 1) multiAppCount++;
    for (const app of apps) {
      appCounts[app] = (appCounts[app] || 0) + 1;
    }
  }

  return (
    <AdminLayout>
      <div className="space-y-6 animate-fade-in">
        <div>
          <h1 className="text-3xl font-semibold tracking-tight">Gestion des accès</h1>
          <p className="text-muted-foreground">UserAppAccess — vue par entreprise, accès par app et rôle</p>
        </div>

        {/* Stats */}
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          {Object.entries(APP_LABELS).map(([key, label]) => {
            const Icon = APP_ICONS[key] || Globe;
            return (
              <Card key={key}>
                <CardHeader className="flex flex-row items-center justify-between pb-2">
                  <CardTitle className="text-sm font-medium text-muted-foreground">{label}</CardTitle>
                  <Icon className="h-4 w-4 text-primary" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{isLoading ? "—" : appCounts[key] || 0}</div>
                  <p className="text-xs text-muted-foreground">entreprises</p>
                </CardContent>
              </Card>
            );
          })}
        </div>

        <div className="flex items-center gap-4">
          <Card className="flex-1">
            <CardContent className="p-4 flex items-center gap-3">
              <Users className="h-5 w-5 text-primary" />
              <div>
                <div className="text-lg font-bold">{multiAppCount}</div>
                <div className="text-xs text-muted-foreground">entreprises multi-apps</div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Search + Company list */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Rechercher une entreprise…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10"
          />
        </div>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <Building2 className="h-4 w-4" />
              Entreprises et accès
              {!isLoading && <span className="text-xs text-muted-foreground font-normal">({items.length})</span>}
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {isLoading ? (
              <div className="p-6 space-y-2">
                {Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} className="h-12 w-full" />)}
              </div>
            ) : items.length === 0 ? (
              <div className="p-6 text-center text-muted-foreground">Aucune entreprise trouvée</div>
            ) : (
              items.map((c: any) => <CompanyAccessRow key={c.id} company={c} />)
            )}
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
}
